/**
 * ControlsBar — date range picker + analyze / re-analyze / disconnect controls.
 *
 * The AI insights modal and subject-analysis panels were removed on 2026-08-13:
 * they were driven by a browser-side DeepSeek call that sent Gmail-derived
 * contact addresses to a third party. See the module README.
 */

import React, { useEffect, useState } from 'react';
import { Button, DatePicker, Space } from 'antd';
import { motion } from 'framer-motion';
import { Search, LogOut, RefreshCw } from 'lucide-react';
import dayjs from 'dayjs';
import { DATE_PRESETS, FADE_IN, DEFAULT_DAYS } from '../constants';

const { RangePicker } = DatePicker;


export function ControlsBar({
  onAnalyze, onDisconnect, loading, hasData, hideDisconnect = false,
  isDark, onRangeChange,
}) {
  const [dates, setDates] = useState([dayjs().subtract(DEFAULT_DAYS, 'day'), dayjs()]);

  useEffect(() => {
    if (onRangeChange) onRangeChange(dates);
  }, [dates, onRangeChange]);

  const applyPreset = (days) => setDates([dayjs().subtract(days, 'day'), dayjs()]);

  const handleAnalyze = () => {
    if (!dates[0] || !dates[1]) return;
    onAnalyze(dates[0].toDate(), dates[1].toDate());
  };

  return (
    <>
      <motion.div
        variants={FADE_IN} initial="hidden" animate="visible"
        style={{
          display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
          padding: '12px 20px', marginBottom: 20, borderRadius: 14,
          background: isDark ? 'rgba(255,255,255,0.03)' : '#ffffff',
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : '#f0f0f0'}`,
          boxShadow: isDark ? '0 1px 3px rgba(0,0,0,0.2)' : '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
        }}
      >
        <Space size={4}>
          {DATE_PRESETS.map(({ label, days }) => {
            const isActive = dates[0] && dates[1] && dayjs().subtract(days, 'day').isSame(dates[0], 'day') && dayjs().isSame(dates[1], 'day');
            return (
              <Button
                key={label} size="small" type={isActive ? 'primary' : 'default'}
                onClick={() => applyPreset(days)}
                style={{
                  borderRadius: 8, fontWeight: 600, fontSize: 12, height: 30, padding: '0 12px',
                  ...(isActive ? {
                    background: isDark ? 'rgba(99,102,241,0.2)' : 'rgba(99,102,241,0.1)',
                    color: '#6366f1',
                    borderColor: isDark ? 'rgba(99,102,241,0.4)' : 'rgba(99,102,241,0.3)',
                  } : {}),
                }}
              >
                {label}
              </Button>
            );
          })}
        </Space>

        <div style={{ width: 1, height: 26, background: isDark ? 'rgba(255,255,255,0.08)' : '#e5e7eb', flexShrink: 0 }} />

        <RangePicker
          value={dates} onChange={setDates}
          disabledDate={(d) => d && d.isAfter(dayjs())}
          format="MMM D, YYYY" allowClear={false} style={{ borderRadius: 8 }} size="middle"
        />

        <div style={{ flex: 1 }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ position: 'relative' }}>
            {!hasData && !loading && (
              <motion.div
                aria-hidden
                animate={{ scale: [1, 1.18, 1], opacity: [0.45, 0, 0.45] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
                style={{
                  position: 'absolute',
                  inset: -5,
                  borderRadius: 14,
                  border: '2px solid rgba(99,102,241,0.45)',
                  pointerEvents: 'none',
                }}
              />
            )}
            <Button
              type="primary"
              icon={hasData ? <RefreshCw size={15} /> : <Search size={15} />}
              loading={loading} onClick={handleAnalyze}
              style={{
                borderRadius: 11, fontWeight: 750, height: 42, padding: '0 22px',
                fontSize: 14.5, letterSpacing: '-0.1px',
                background: 'linear-gradient(135deg, #6366f1 0%, #7c3aed 100%)',
                border: 'none',
                boxShadow: hasData
                  ? '0 2px 8px rgba(99,102,241,0.28)'
                  : '0 8px 22px rgba(99,102,241,0.42)',
              }}
            >
              {hasData ? 'Re-analyze' : 'Analyze'}
            </Button>
          </div>

          {!hideDisconnect && (
            <Button
              icon={<LogOut size={13} />} onClick={onDisconnect}
              style={{
                borderRadius: 8, fontWeight: 600, height: 34,
                background: isDark ? 'rgba(220,38,38,0.08)' : '#fef2f2',
                color: '#dc2626',
                border: `1px solid ${isDark ? 'rgba(220,38,38,0.2)' : '#fecaca'}`,
              }}
            >
              Disconnect
            </Button>
          )}
        </div>
      </motion.div>
    </>
  );
}
